import { ApolloError } from '@apollo/client';
import {
  useCascade,
  UseCascadeResult,
  useMutation,
  useTranslation,
} from '@gimlite/common';
import { WizardItemType, Zone } from '@gimlite/ui-v1';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Mutation,
  MutationCreateContractArgs,
} from '../../../../client/graphql';
import {
  convertCredentialsFrontToBack,
  CredentialType,
} from '../../../../common/mapper/credential.mapper';
import {
  ContractMotoristLine,
  statusHelper,
} from '../../../../composition/line/contract-motorist.line';

import { createContractGQL } from '../../../../common/gql/createContract.gql';
import { ContractWizardInfos } from '../../../../composition/wizard-infos/contract.wizard-infos';
import {
  SummaryForm,
  SummaryFormType,
} from '../../../../form/summary/summary.form';
import { AddSeveralMotoristsToAnExistingPoolContractWizardContext } from '../../addSeveralMotoristsToAnExistingPoolContract.wizard';

type ResultPosibility = any;
// | Mutation['createMotorist']
// | Mutation['createLocalMotorist'];

export const SummaryStep = ({
  context: {
    parkingId,
    contractBase,
    contractMode,
    contractsDetails,
    subPoolContractId,
    subPoolExistedSave,
    product: { id: productId },
  },
  validate,
  submit,
}: WizardItemType.Config.Component<AddSeveralMotoristsToAnExistingPoolContractWizardContext>) => {
  const { t, lang } = useTranslation();

  const [created, setCreated] = useState<SummaryFormType.Data.Line[]>();
  const [createdToExport, setCreatedExport] =
    useState<Array<SummaryFormType.Data.Created>>();

  const [createContractCall] = useMutation<
    { createContract: Mutation['createContract'] },
    MutationCreateContractArgs
  >(createContractGQL);

  const { isFinish } = useCascade<ResultPosibility>([
    ...(contractMode === 'create'
      ? contractsDetails.created.flatMap((contract) => [
          async (
            prevResult: Array<UseCascadeResult<ResultPosibility>>,
            index: number,
          ) => {
            const prevNearResut = prevResult?.[index - 1];

            try {
              const response = await createContractCall({
                variables: {
                  input: {
                    parkingId: parkingId!,
                    productId: productId!,
                    startDate: contractBase.startDate!,
                    stopDate: contractBase.endDate!,
                    description: contractBase.description,
                    reference: contractBase.reference,
                    antiPassBackMode: contractBase.APBMode,
                    isEnabled: contractBase.state,
                    name1: contract.firstName,
                    name2: contract.lastName,
                    lang: contract.lang,
                    username: contract.email,
                    sendInvitation: contract.sendInvitation,
                    articleCode: contractBase.articleCode!,
                    linkedContractId: subPoolContractId,
                    freeFlagIsEnabled:
                      contractBase.APBMode !== 'NONE'
                        ? !contractBase.APBNext
                        : null,
                    credentials: convertCredentialsFrontToBack(
                      [
                        {
                          value: contract.visibleId,
                          description: contract.uid,
                          type: 'uid',
                        },
                        {
                          value: contract.plate,
                          description: null,
                          type: 'plate',
                        },
                      ].reduce(
                        (
                          acc: CredentialType.CredentialFromFront[],
                          { value, type, description },
                        ) => {
                          if (type === 'plate' && value) {
                            return [
                              ...acc,
                              {
                                name: 'PLATE',
                                plate: value.toUpperCase(),
                                description: '',
                              } as CredentialType.CredentialFromFront,
                            ];
                          } else if (type === 'uid' && value) {
                            return [
                              ...acc,
                              {
                                name: 'RFID',
                                visibleId: value,
                                uid: description,
                              } as CredentialType.CredentialFromFront,
                            ];
                          } else {
                            return acc;
                          }
                        },
                        [],
                      ),
                    ).map(({ _id, ...rest }) => ({
                      ...rest,
                    })),
                  },
                },
              });

              if (
                response?.errors &&
                Array.isArray(response?.errors) &&
                response?.errors.length > 0
              ) {
                throw response?.errors[0]?.message;
              }

              const createContract = response?.data?.createContract;

              setCreated((list) => [
                ...(list ? list : []),
                {
                  _id: uuidv4(),
                  items: {
                    component: (
                      <ContractMotoristLine
                        data={{
                          firstName: createContract?.name1 || null,
                          contractId: createContract?.ospContractId || null,
                          lastName: createContract?.name2 || null,
                          status: statusHelper({
                            isEnabled: createContract?.isEnabled!,
                            startDate: new Date(createContract?.startDate!),
                            stopDate: new Date(createContract?.stopDate!),
                          }),
                        }}
                        config={{ action: false }}
                      />
                    ),
                    state: { type: 'success' },
                  },
                },
              ]);

              return { title: 'CreateContract', response: createContract };
            } catch (error: unknown) {
              setCreated((list) => [
                ...(list ? list : []),
                {
                  _id: uuidv4(),
                  items: {
                    component: (
                      <ContractMotoristLine
                        data={{
                          firstName: contract.firstName,
                          contractId: null,
                          lastName: contract.lastName,
                          status: contractBase.state ? 'ACTIVE' : 'SUSPENDED',
                        }}
                        config={{ action: false }}
                      />
                    ),
                    state: {
                      type: 'error',
                      message:
                        error instanceof ApolloError
                          ? `${error.message}`
                          : `${error}`,
                    },
                  },
                },
              ]);

              return {
                title: 'CreateContract',
                response: new Error(
                  error instanceof ApolloError
                    ? `${error.message}`
                    : `${error}`,
                ),
              };
            }
          },
        ])
      : contractsDetails.checked.map((contract) => async () => {
          const _id = uuidv4();

          try {
            const response = await createContractCall({
              variables: {
                input: {
                  parkingId: parkingId!,
                  productId: productId!,
                  startDate: contractBase.startDate!,
                  stopDate: contractBase.endDate!,
                  description: contractBase.description,
                  reference: contractBase.reference,
                  antiPassBackMode: contractBase.APBMode,
                  isEnabled: contractBase.state,
                  name1: contract.name1,
                  name2: contract.name2,
                  lang: contract.lang,
                  username: contract.username,
                  motoristId: contract.motoristId,
                  address:{
                    address1:contract.address?.address1 || undefined,
                    address2:contract.address?.address2 || undefined,
                    zipcode:contract.address?.zipcode  || undefined,
                    city:contract.address?.city || undefined,
                    country:contract.address?.country || undefined,
                  },
                  shortUid: contract.shortUid,
                  sendInvitation: true,
                  articleCode: contractBase.articleCode!,
                  linkedContractId: subPoolContractId,
                  freeFlagIsEnabled:
                    contractBase.APBMode !== 'NONE'
                      ? !contractBase.APBNext
                      : null,
                  credentials: (contract?.credentials || []).map(
                    ({ __typename, ...rest }) => ({
                      ...rest,
                    }),
                  ),
                },
              },
            });

            if (
              response?.errors &&
              Array.isArray(response?.errors) &&
              response?.errors.length > 0
            ) {
              throw response?.errors[0]?.message;
            }

            const createContract = response?.data?.createContract;

            setCreated((list) => [
              ...(list ? list : []),
              {
                _id,
                items: {
                  component: (
                    <ContractMotoristLine
                      data={{
                        firstName: createContract?.name1 || null,
                        contractId: createContract?.ospContractId || null,
                        lastName: createContract?.name2 || null,
                        status: statusHelper({
                          isEnabled: createContract?.isEnabled!,
                          startDate: new Date(createContract?.startDate!),
                          stopDate: new Date(createContract?.stopDate!),
                        }),
                      }}
                      config={{ action: false }}
                    />
                  ),
                  state: { type: 'success' },
                },
              },
            ]);

            return { title: 'CreateContract', response: createContract };
          } catch (error: unknown) {
            setCreated((list) => [
              ...(list ? list : []),
              {
                _id,
                items: {
                  component: (
                    <ContractMotoristLine
                      data={{
                        firstName: contract.name1 || null,
                        contractId: null,
                        lastName: contract.name2 || null,
                        status: contractBase.state ? 'ACTIVE' : 'SUSPENDED',
                      }}
                      config={{ action: false }}
                    />
                  ),
                  state: {
                    type: 'error',
                    message:
                      error instanceof ApolloError
                        ? `${error.message}`
                        : `${error}`,
                  },
                },
              },
            ]);

            return {
              title: 'CreateContract',
              response: new Error(
                error instanceof ApolloError ? `${error.message}` : `${error}`,
              ),
            };
          }
        })),
  ]);

  useEffect(() => {
    validate(isFinish);
  }, [isFinish]);

  return (
    <Zone
      config={{
        gap: {
          y: 1,
          x: 1,
        },
        zones: [['info', 'list']],
        rows: ['1fr'],
        columns: ['min-content', '1fr'],
      }}
    >
      <Zone.Area config={{ area: 'info' }}>
        <ContractWizardInfos
          data={{
            APBMode: contractBase.APBMode,
            APBNext:
              contractBase.APBMode !== 'NONE' ? contractBase.APBNext : null,
            state: contractBase.state,
            startDate: contractBase.startDate,
            endDate: contractBase.endDate,
            description: contractBase.description,
            reference: contractBase.reference,
            articleCode: contractBase.articleCode,
          }}
        />
      </Zone.Area>

      <Zone.Area config={{ area: 'list' }}>
        <SummaryForm
          data={{
            list: created,
            created: createdToExport,
          }}
        />
      </Zone.Area>
    </Zone>
  );
};
